import {
  COMPANY_FIELD_PREFIX,
  EMPTY_FILTER,
  FILTER_DAY_OPERATORS,
  FILTER_EMAIL_DAYS,
  FILTER_JSON_MAX_LENGTH,
  FILTER_TASK_DAY_OPERATORS,
  filterConditionSchema,
  filterDefinitionSchema,
  filterJsonSchema,
  isEmptyFilter,
  systemFilterType,
  type FilterCondition,
  type FilterDefinition,
} from "@gioia365/contracts";

// La bozza dei filtri dell'elenco Lead vive nell'indirizzo (piano M3, Task 4): `filter` è il JSON della definizione
// (`FilterDefinition` v1), assente quando non c'è nessun filtro, così un link, «Indietro» o una ricarica ritrovano la
// stessa vista. Nell'indirizzo vanno soltanto le condizioni complete: una scelta senza voci, un numero vuoto o non
// valido, «il giorno…» o «entro il giorno…» senza il giorno, «negli ultimi … giorni» senza un numero di giorni valido restano nella
// bozza del pannello e non restringono nulla.

/** Una condizione del pannello mentre la si sceglie: può essere ancora incompleta. */
export type DraftCondition =
  | { kind: "choice"; field: string; op: Extract<FilterCondition, { kind: "choice" }>["op"]; values: string[] }
  | { kind: "tags"; field: "tags"; op: Extract<FilterCondition, { kind: "tags" }>["op"]; values: string[] }
  | { kind: "number"; field: string; op: Extract<FilterCondition, { kind: "number" }>["op"]; text: string }
  | { kind: "date"; field: string; op: Extract<FilterCondition, { kind: "date" }>["op"]; day: string }
  | { kind: "task"; field: "nextTaskAt"; op: Extract<FilterCondition, { kind: "task" }>["op"]; day: string }
  | { kind: "email"; field: "emailClicked"; op: Extract<FilterCondition, { kind: "email" }>["op"]; text: string };

/** Il massimo che il server accetta in un filtro numerico. */
const NUMBER_MAX = 1_000_000;

/**
 * Il pannello di M3 costruisce soltanto la radice «tutte» con una condizione per campo, del tipo del campo (piano M3,
 * D1): un indirizzo con altro (gruppi, «almeno una», due condizioni sullo stesso campo) filtrerebbe con condizioni che
 * il pannello non sa mostrare, e vale come vuoto.
 */
function isPanelShape(definition: FilterDefinition): boolean {
  if (definition.combinator !== "all" || definition.groups.length > 0) return false;
  const fields = new Set(definition.conditions.map((condition) => condition.field));
  if (fields.size !== definition.conditions.length) return false;
  return definition.conditions.every((condition) => {
    const type = systemFilterType(condition.field);
    if (type !== null) return type === condition.kind;
    return condition.field.startsWith(COMPANY_FIELD_PREFIX) && (condition.kind === "choice" || condition.kind === "number");
  });
}

/**
 * Il parametro `filter` dell'indirizzo. Il router legge da sé un JSON scritto nell'indirizzo, quindi può arrivare il
 * testo o già l'oggetto: una forma sbagliata, troppo lunga per il server o che il pannello non sa mostrare vale
 * `EMPTY_FILTER`.
 */
export function parseFilterParam(raw: unknown): FilterDefinition {
  const parsed =
    typeof raw === "string" ? filterJsonSchema.safeParse(raw) : typeof raw === "object" && raw !== null ? filterDefinitionSchema.safeParse(raw) : null;
  if (!parsed?.success) return EMPTY_FILTER;
  const definition = parsed.data;
  if (!isPanelShape(definition) || JSON.stringify(definition).length > FILTER_JSON_MAX_LENGTH) return EMPTY_FILTER;
  return definition;
}

/** Il testo del parametro `filter`; `undefined` per il filtro vuoto, che nell'indirizzo non si scrive. */
export function serializeFilter(definition: FilterDefinition): string | undefined {
  return isEmptyFilter(definition) ? undefined : JSON.stringify(definition);
}

/** Vero per il numero scritto che non va: non intero, negativo o troppo grande. Il campo vuoto non è un errore. */
export function isInvalidNumber(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === "") return false;
  return !/^\d+$/.test(trimmed) || Number(trimmed) > NUMBER_MAX;
}

/** Vero per i giorni scritti che non vanno: non un intero da 1 a 365. Il campo vuoto non è un errore. */
export function isInvalidDays(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === "") return false;
  return !/^\d+$/.test(trimmed) || Number(trimmed) < FILTER_EMAIL_DAYS.min || Number(trimmed) > FILTER_EMAIL_DAYS.max;
}

/** La condizione che va al server, oppure `null` se la bozza non restringe ancora nulla. */
export function completeCondition(draft: DraftCondition): FilterCondition | null {
  let candidate: unknown;
  switch (draft.kind) {
    case "choice":
    case "tags":
      candidate = { kind: draft.kind, field: draft.field, op: draft.op, values: draft.values };
      break;
    case "number":
      if (draft.text.trim() === "" || isInvalidNumber(draft.text)) return null;
      candidate = { kind: "number", field: draft.field, op: draft.op, value: Number(draft.text.trim()) };
      break;
    case "date":
      candidate = (FILTER_DAY_OPERATORS as readonly string[]).includes(draft.op)
        ? { kind: "date", field: draft.field, op: draft.op, day: draft.day }
        : { kind: "date", field: draft.field, op: draft.op };
      break;
    case "task":
      // «Entro il giorno…» senza il giorno non restringe ancora nulla (lo ferma il contratto).
      candidate = (FILTER_TASK_DAY_OPERATORS as readonly string[]).includes(draft.op)
        ? { kind: "task", field: draft.field, op: draft.op, day: draft.day }
        : { kind: "task", field: draft.field, op: draft.op };
      break;
    case "email":
      // «Negli ultimi … giorni» senza un numero di giorni valido non restringe ancora nulla.
      if (draft.op === "within" && (draft.text.trim() === "" || isInvalidDays(draft.text))) return null;
      candidate = draft.op === "within" ? { kind: "email", field: draft.field, op: draft.op, days: Number(draft.text.trim()) } : { kind: "email", field: draft.field, op: draft.op };
      break;
  }
  const parsed = filterConditionSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

/** La definizione delle sole condizioni complete della bozza, nell'ordine della bozza. */
export function definitionOf(draft: readonly DraftCondition[]): FilterDefinition {
  const conditions = draft.map(completeCondition).filter((condition): condition is FilterCondition => condition !== null);
  return { version: 1, combinator: "all", conditions, groups: [] };
}

/** La bozza di partenza di una definizione: ogni condizione completa. */
export function draftOf(definition: FilterDefinition): DraftCondition[] {
  return definition.conditions.map((condition): DraftCondition => {
    switch (condition.kind) {
      case "choice":
        return { kind: "choice", field: condition.field, op: condition.op, values: [...condition.values] };
      case "tags":
        return { kind: "tags", field: "tags", op: condition.op, values: [...condition.values] };
      case "number":
        return { kind: "number", field: condition.field, op: condition.op, text: String(condition.value) };
      case "date":
        return { kind: "date", field: condition.field, op: condition.op, day: condition.day ?? "" };
      case "task":
        return { kind: "task", field: condition.field, op: condition.op, day: condition.day ?? "" };
      case "email":
        return { kind: "email", field: condition.field, op: condition.op, text: condition.days === undefined ? "" : String(condition.days) };
    }
  });
}
